import { createSelector } from "@reduxjs/toolkit"

import { historyAPI } from "store/store.js"
import { annotateTripPairs } from "store/functions/tripPairs.js"

/**
 * @fileoverview Selectors for map-related state in the Redux store.
 *
 * This file contains selector functions that derive map-specific data from the
 * overall application state, such as retrieving active map layers, filter settings,
 * and other map configuration details.
 *
 * These selectors help encapsulate the logic for accessing and transforming
 * map-related state, promoting modularity and maintainability in the codebase.
 *
 * @module store/reducers/selectors/mapSelectors
 */

const staticModelSettings = require('store/data/model_configs.json');

/**
 * Selects the current map mode from the Redux state.
 *
 * Retrieves the configured map mode used to determine how the map should behave
 * or be presented (e.g., viewing/editing modes), as defined in the application's
 * general mapping settings.
 *
 * @function selectMapMode
 * @param {Object} state - The Redux store state.
 * @returns {*} The current map mode value from `state.inputData.present.generalSettings.mapping.mapMode`.
 */
export const selectMapMode = state => {
  return state.inputData.present.generalSettings.mapping.mapMode
}

/**
 * Selects a GeoJSON FeatureCollection containing only dropped location points.
 *
 * Filters `state.inputData.present.geoJson.features` to include only features where:
 * - `properties.isDrop` is truthy, and
 * - `properties.type_` is exactly `'location'`.
 *
 * If the GeoJSON structure is missing or invalid, returns an empty FeatureCollection.
 *
 * @function selectDroppedLocationGeoJson
 * @param {Object} state - The Redux store state.
 * @returns {{type: 'FeatureCollection', features: Array}} A GeoJSON FeatureCollection of dropped location features.
 */
// Memoized: returns a stable reference when geoJson.features is unchanged, so
// unrelated dispatches (e.g. the 3s solve-status poll) don't force the heavy map
// to re-render. Without this, the `.filter(...)` wrapped in a new object literal
// produced a fresh reference on every call, re-rendering the map on every action.
export const selectDroppedLocationGeoJson = createSelector(
  [state => state?.inputData?.present?.geoJson?.features],
  features => {
    if (!Array.isArray(features)) {
      return { type: 'FeatureCollection', features: [] }
    }

    const droppedLocationFeatures = features.filter(
      feature => feature?.properties?.isDrop && feature?.properties?.type_ === 'location'
    )

    return { type: 'FeatureCollection', features: droppedLocationFeatures }
  }
)

/**
 * Creates a selector that returns "trip" features that start and/or end at a given location UUID.
 *
 * This selector inspects `state.inputData.present.geoJson.features` and filters features by
 * matching `feature.properties.fromUuid` and/or `feature.properties.toUuid` against the provided `uuid`.
 *
 * Behavior:
 * - If `uuid` is falsy, returns `[]`.
 * - If `includeTo` and `includeFrom` are both `true`, returns trips where `fromUuid === uuid || toUuid === uuid`.
 * - If only `includeTo` is `true`, returns trips where `toUuid === uuid`.
 * - If only `includeFrom` is `true`, returns trips where `fromUuid === uuid`.
 * - If neither is `true`, returns `[]`.
 *
 * The selector is defensive: if state shape is missing/invalid or `features` is not an array, it returns `[]`.
 *
 * @param {Object} params
 * @param {string} params.uuid - Location UUID to match.
 * @param {boolean} [params.includeTo=true] - Include trips ending at the location (`toUuid`).
 * @param {boolean} [params.includeFrom=true] - Include trips starting at the location (`fromUuid`).
 * @returns {(state: Object) => Array} Selector that returns an array of matching features.
 */
export const selectTripsAtPoint =
  ({ uuid, includeTo = true, includeFrom = true }) =>
  state => {
    if (!uuid) return []

    const features = state?.inputData?.present?.geoJson?.features
    if (!Array.isArray(features) || (!includeTo && !includeFrom)) return []

    const matches = feature => {
      const props = feature?.properties
      if (!props) return false

      if (includeTo && includeFrom) {
        return props.fromUuid === uuid || props.toUuid === uuid
      }

      if (includeTo) return props.toUuid === uuid
      return props.fromUuid === uuid
    }

    return features.filter(matches)
  }


/**
 * Selects a GeoJSON FeatureCollection containing only "dropped" trip-related features.
 *
 * Filters `state.inputData.present.geoJson.features` to include only features where:
 * - `properties.isDrop` is truthy, and
 * - `properties.type` is one of: `'trip' | 'trip_start' | 'trip_end'`.
 *
 * Notes:
 * - Returns an empty FeatureCollection if the GeoJSON structure is missing/invalid.
 * - Defensive against missing `properties` on individual features.
 *
 * @function selectDroppedTripGeoJson
 * @param {Object} state - The Redux store state.
 * @returns {{type: 'FeatureCollection', features: Array}} A GeoJSON FeatureCollection of dropped trip features.
 */
// Memoized (see selectDroppedLocationGeoJson) so poll dispatches don't re-render.
export const selectDroppedTripGeoJson = createSelector(
  [state => state?.inputData?.present?.geoJson?.features],
  features => {
    if (!Array.isArray(features)) {
      return { type: 'FeatureCollection', features: [] }
    }

    const droppedTripTypes = new Set(['trip', 'trip_start', 'trip_end'])

    const droppedTripFeatures = features.filter(feature => {
      const props = feature?.properties
      return Boolean(props?.isDrop) && droppedTripTypes.has(props?.type)
    })

    return { type: 'FeatureCollection', features: droppedTripFeatures }
  }
)

/**
 * Selects a GeoJSON feature by its UUID from the Redux state.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.uuid - The UUID of the GeoJSON feature to select.
 * @returns {function(Object): (Object|undefined)} A selector function that takes the Redux state and returns the matching GeoJSON feature, or undefined if not found.
 */
export const selectGeoJsonByUuid =
  ({ uuid }) =>
  state => {
    if (!uuid) return undefined

    const features = state?.inputData?.present?.geoJson?.features
    if (!Array.isArray(features)) return undefined

    return features.find(feature => feature?.properties?.uuid === uuid)
  }


/**
 * Selects the current GeoJSON filter configuration from the Redux state.
 *
 * Retrieves `state.inputData.present.geoJsonFilter`.
 *
 * Defensive behavior:
 * - If the state path is missing, returns an empty object.
 * - If `geoJsonFilter` is not a plain object (e.g., null/array/primitive), returns an empty object.
 *
 * @function selectGeoJsonFilters
 * @param {Object} state - The Redux store state.
 * @returns {Object} The GeoJSON filter configuration object (or `{}` as a safe default).
 */
export const selectGeoJsonFilters = state => {
  const filters = state?.inputData?.present?.geoJsonFilter

  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
    return {}
  }

  return filters
}

/**
 * Selects a GeoJSON FeatureCollection containing only *result* trip route legs as LineStrings.
 *
 * This selector:
 * - Starts from `state.inputData.present.geoJson.features`
 * - Keeps only features that are:
 *   - `properties.isResult === true`
 *   - `properties.type === 'routeLeg'`
 *   - `geometry.type === 'LineString'`
 *   - NOT dropped (`properties.isDrop` is falsy)
 * - Excludes any result features whose `properties.vehicleId` matches an ID in
 *   `state.inputData.present.geoJsonFilter.vehicleIds` (if provided).
 *
 * Defensive behavior:
 * - Returns an empty FeatureCollection if the GeoJSON feature array is missing/invalid.
 * - Treats `geoJsonFilter.vehicleIds` as empty if missing/invalid.
 *
 * @function selectResultTripsGeoJson
 * @param {Object} state - The Redux store state.
 * @returns {{type: 'FeatureCollection', features: Array}} A GeoJSON FeatureCollection of filtered result trip features.
 */
// Memoized (see selectDroppedLocationGeoJson). Inputs are the feature array plus
// the two filter values it depends on, so it recomputes only when one of those
// actually changes — not on every poll dispatch.
export const selectResultTripsGeoJson = createSelector(
  [
    state => state?.inputData?.present?.geoJson?.features,
    state => state?.inputData?.present?.geoJsonFilter?.resultsLayerVisible,
    state => state?.inputData?.present?.geoJsonFilter?.vehicleIds,
  ],
  (features, resultsLayerVisible, vehicleIdsRaw) => {
    if (!Array.isArray(features)) {
      return { type: 'FeatureCollection', features: [] }
    }
    const resultsEnabled = Boolean(resultsLayerVisible)

    if (!resultsEnabled) {
      return { type: 'FeatureCollection', features: [] }
    }

    const excludedVehicleIds = new Set(
      Array.isArray(vehicleIdsRaw) ? vehicleIdsRaw.map(id => String(id)) : []
    )

    const filtered = features.filter(feature => {
      const props = feature?.properties
      const geom = feature?.geometry
      if (!props || !geom) return false

      if (!props.isResult) return false
      if (props.type !== 'routeLeg') return false
      if (geom.type !== 'LineString') return false
      if (props.isDrop) return false

      const vehicleId = props.vehicleId != null ? String(props.vehicleId) : ''
      return !excludedVehicleIds.has(vehicleId)
    })

    return { type: 'FeatureCollection', features: filtered }
  }
)


/**
 * Creates a selector that returns a named value from `state.inputData.present.geoJsonFilter`.
 *
 * Defensive behavior:
 * - If `filterName` is not a non-empty string, returns a selector that yields `undefined`.
 * - If the state path is missing/invalid (e.g., `geoJsonFilter` is null/array/primitive),
 *   the selector yields `undefined`.
 * - If the named filter does not exist, yields `undefined`.
 *
 * @function selectGeoJsonFilter
 * @param {string} filterName - The key of the filter to retrieve from `geoJsonFilter`.
 * @returns {(state: Object) => *} Selector that returns the filter value (or `undefined`).
 */
export const selectGeoJsonFilter = filterName => state => {
  if (typeof filterName !== 'string' || filterName.trim() === '') return undefined

  const filters = state?.inputData?.present?.geoJsonFilter
  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) return undefined

  return filters[filterName]
}


/**
 * Alias for {@link selectGeoJsonFilter}.
 *
 * Kept for backward compatibility with existing imports/usages.
 *
 * @function selectFilterState
 * @param {string} filterName - The key of the filter to retrieve from `geoJsonFilter`.
 * @returns {(state: Object) => *} Selector that returns the filter value (or `undefined`).
 */
export const selectFilterState = filterName => selectGeoJsonFilter(filterName)


/**
 * Selects whether trips should be shown on the map.
 *
 * Trips are tied to the location layer — they connect locations, so there is
 * no separate trip toggle. They show when both:
 * - The location layer is visible: `state.inputData.present.geoJsonFilter.locationLayerVisible`
 * - The active model supports trips: `staticModelSettings[activeModelType].mapping.trip_editor`
 *
 * Defensive behavior:
 * - Returns `false` if required state paths are missing/invalid.
 * - Returns `false` if `staticModelSettings` does not contain the active model type
 *   or the expected `mapping.trip_editor` flag.
 *
 * @function selectTripLayerEnabled
 * @param {Object} state - The Redux store state.
 * @returns {boolean} True if the location layer is visible and the active model supports the trip editor.
 */
export const selectTripLayerEnabled = state => {
  const locationLayerVisible = Boolean(
    state?.inputData?.present?.geoJsonFilter?.locationLayerVisible
  )

  const activeModelType = state?.inputData?.present?.activeModelType
  const tripLayerEnabled = Boolean(
    staticModelSettings?.[activeModelType]?.mapping?.trip_editor
  )

  return locationLayerVisible && tripLayerEnabled
}

/**
 * Selects the scenario's coordinate system.
 *
 * A property of the *data*, not of the problem being solved, so it is a
 * per-scenario setting (`generalSettings.mapping.coordinateSystem`) rather
 * than a model attribute — any model can run on either kind of data:
 * - `'geographic'` — latitude/longitude on a Mapbox map. The default when
 *   absent, so scenarios saved before the setting existed keep working.
 * - `'cartesian'` — arbitrary x-y coordinates on an orthographic plot; geo-only
 *   features (geocoding, haversine/driving costs, basemap) must not run.
 *
 * This is the single discriminator for coordinate-system branching — gate on
 * this selector rather than comparing model types.
 *
 * @function selectCoordinateSystem
 * @param {Object} state - The Redux store state.
 * @returns {'geographic'|'cartesian'} The scenario's coordinate system.
 */
export const selectCoordinateSystem = state =>
  state?.inputData?.present?.generalSettings?.mapping?.coordinateSystem ?? 'geographic'

/**
 * Convenience boolean form of {@link selectCoordinateSystem}.
 *
 * @function selectIsCartesian
 * @param {Object} state - The Redux store state.
 * @returns {boolean} True when the scenario uses cartesian x-y coordinates.
 */
export const selectIsCartesian = state => selectCoordinateSystem(state) === 'cartesian'

/**
 * Creates a selector that returns GeoJSON filtered according to UI filter state and caller options.
 *
 * This selector primarily filters `state.inputData.present.geoJson.features` using:
 * - UI toggles in `state.inputData.present.geoJsonFilter` (layer visibility, vehicleIds)
 * - `props` flags (excludeTrips, excludeResults, resultsOnly, dropsOnly, etc.)
 *
 * Notes / compatibility:
 * - Preserves original filter order and behavior (e.g., `resultsOnly` applied after excluding results).
 * - Returns `[]` when `props.excludeAll` is truthy (matches original behavior).
 * - Adds defensive validation: missing/invalid state paths will yield an empty FeatureCollection output
 *   rather than throwing.
 *
 * @param {Object} [props]
 * @param {boolean} [props.excludeAll=false]
 * @param {boolean} [props.excludeTrips=false]
 * @param {boolean} [props.excludeResults=false]
 * @param {boolean} [props.resultsOnly=false]
 * @param {boolean} [props.excludeLineStrings=false]
 * @param {boolean} [props.excludePoints=false]
 * @param {boolean} [props.dropsOnly=false]
 * @param {boolean} [props.excludeDrops=false]
 * @param {boolean|null|undefined} [props.excludeNulls] - Defaults to `true` when null/undefined.
 * @returns {(state: Object) => (Array|Object)} Selector returning `[]` if excludeAll, else an object
 *   shaped like geoJson with an additional `geoJsonData` property containing filtered features.
 */
const EMPTY_FEATURE_COLLECTION = { type: 'FeatureCollection', features: [] }

/** Stable value returned for `excludeAll`, so the reference never changes. */
const EXCLUDE_ALL_RESULT = []
const selectExcludeAll = () => EXCLUDE_ALL_RESULT

const selectGeoJsonRoot = state => state?.inputData?.present?.geoJson
const selectGeoJsonFilterRoot = state => state?.inputData?.present?.geoJsonFilter

const buildFilteredData = (props, rawGeoJson, filters) => {
    const {
      excludeTrips = false,
      excludeResults = false,
      resultsOnly = false,
      excludeLineStrings = false,
      excludePoints = false,
      dropsOnly = false,
      excludeDrops = false,
    } = props

    // Keep original defaulting behavior: null/undefined => true, otherwise use provided value.
    const excludeNulls = props.excludeNulls ?? true

    const baseGeoJson =
      rawGeoJson && typeof rawGeoJson === 'object'
        ? rawGeoJson
        : EMPTY_FEATURE_COLLECTION

    const rawFeatures = baseGeoJson?.features
    let features = Array.isArray(rawFeatures) ? [...rawFeatures] : []

    const safeFilters =
      filters && typeof filters === 'object' && !Array.isArray(filters) ? filters : {}

    // Vehicle exclusions (only excludes result features matching a vehicleId)
    const vehicleIds = Array.isArray(safeFilters.vehicleIds) ? safeFilters.vehicleIds : []
    for (const elem of vehicleIds) {
      const vehicleIdStr = String(elem)
      features = features.filter(item => {
        const props = item?.properties
        return !props?.isResult || props?.vehicleId !== vehicleIdStr
      })
    }

    // Trips have no filter of their own — callers gate them via `excludeTrips`
    // (derived from selectTripLayerEnabled: location layer + model support).
    if (excludeTrips) {
      features = features.filter(item => item?.properties?.type !== 'trip')
    }

    if (!safeFilters.locationLayerVisible) {
      features = features.filter(item => item?.properties?.type !== 'point')
    }

    if (!safeFilters.resultsLayerVisible || excludeResults) {
      features = features.filter(item => !item?.properties?.isResult)
    }

    if (!safeFilters.dropLayerVisible) {
      // Preserve original key: `is_drop` (snake_case) instead of `isDrop`
      features = features.filter(item => !item?.properties?.is_drop)
    }

    if (resultsOnly) {
      features = features.filter(item => Boolean(item?.properties?.isResult))
    }

    if (excludeLineStrings) {
      features = features.filter(item => item?.geometry?.type !== 'LineString')
    }

    if (excludePoints) {
      features = features.filter(item => item?.geometry?.type !== 'Point')
    }

    if (dropsOnly) {
      features = features.filter(item => Boolean(item?.properties?.isDrop))
    }

    if (excludeDrops) {
      features = features.filter(item => !item?.properties?.isDrop)
    }

    if (excludeNulls) {
      features = features.filter(item => {
        const coordinates = item?.geometry?.coordinates
        if (!Array.isArray(coordinates)) return false

        // A Point's `coordinates` ARE the two numbers ([x, y]); a LineString's are
        // an array of such pairs. The original predicate only handled the pair
        // shape, so for a Point `elem` is a number and `!0` is true — which
        // silently dropped every feature sitting on an axis. That is not an edge
        // case: the PCB example's depot is at (0, 0), and on a geographic map it
        // would take out anything on the equator or the prime meridian.
        return coordinates.every(elem => {
          if (typeof elem === 'number') return Number.isFinite(elem)
          if (!elem) return false
          return elem[0] !== null && elem[1] !== null
        })
      })
    }

    return { ...baseGeoJson, geoJsonData: { ...baseGeoJson, features } }
}

/**
 * One memoized selector instance per unique `props` combination.
 *
 * The factory is called inline during render (`selectFilteredData({...})`), so
 * a fresh `createSelector` each time would start with an empty cache and never
 * memoize anything. Keying instances by the *values* in `props` means repeated
 * renders reuse the same instance, and reselect can return the identical result
 * object while `geoJson`/`geoJsonFilter` are unchanged — which is what lets
 * `useSelector` skip the re-render, and lets Mapbox skip re-diffing the source.
 */
const filteredDataSelectorCache = new Map()

const propsCacheKey = props => JSON.stringify(props, Object.keys(props).sort())

export const selectFilteredData = (props = {}) => {
  if (props.excludeAll) return selectExcludeAll

  const key = propsCacheKey(props)
  let selector = filteredDataSelectorCache.get(key)
  if (!selector) {
    selector = createSelector(
      [selectGeoJsonRoot, selectGeoJsonFilterRoot],
      (rawGeoJson, filters) => buildFilteredData(props, rawGeoJson, filters)
    )
    filteredDataSelectorCache.set(key, selector)
  }
  return selector
}

// ─── Trip pair grouping ──────────────────────────────────────────────────────

/**
 * `selectFilteredData` with input trips annotated by `pairCount`. Memoized on
 * top of the filtered result, so the annotation pass only re-runs when the
 * filtered features actually change. Grouping logic lives in
 * `store/functions/tripPairs.js` (pure, store-free, unit-tested).
 */
const tripPairSelectorCache = new Map()

export const selectTripDataWithPairCounts = (props = {}) => {
  const key = propsCacheKey(props)
  let selector = tripPairSelectorCache.get(key)
  if (!selector) {
    selector = createSelector([selectFilteredData(props)], filtered => {
      const data = filtered?.geoJsonData
      if (!data) return filtered
      const annotated = annotateTripPairs(data)
      return annotated === data ? filtered : { ...filtered, geoJsonData: annotated }
    })
    tripPairSelectorCache.set(key, selector)
  }
  return selector
}

/**
 * Selector to determine if the last action in the inputData's past or future array
 * was related to adding a row from the map. This is used to determine
 * if we should place the undo/redo buttons on the map interface.
 *
 * @param {Object} [options] - Options object.
 * @param {boolean} [options.lookBackwards=true] - If true, checks past actions; if false, checks future actions.
 * @returns {function(Object): boolean} Selector function that takes state and returns true if the last action was 'addRowFromMap'.
 */
export const selectLastActionMapRelated = ({ lookBackwards = true } = {}) => _state => {

  /*
  Past is a stack and future is a queue: undo pops the END of past onto the
  FRONT of future, and redo shifts that front back off. So the action each
  button would act on is past[last] but future[0] — reading the end of both
  tested the OLDEST pending redo, which only coincided with the right answer
  while exactly one undo had happened. getNextType encodes that asymmetry.

  Preferred because getPast/getFuture copy the whole array on every call, and
  this selector runs inside a useSelector — so that copy happened on every
  dispatch. The array getters remain as a fallback for tests that stub
  historyAPI, and both shapes are tolerated: the middleware now records type
  strings, while an older stub may hold whole action objects.
  */
  const typeOf = (entry) => (typeof entry === 'string' ? entry : entry?.type)

  let nextType
  if (typeof historyAPI.getNextType === 'function') {
    nextType = historyAPI.getNextType(lookBackwards)
  } else {
    const actions = lookBackwards ? historyAPI.getPast?.() : historyAPI.getFuture?.()
    if (!Array.isArray(actions) || actions.length === 0) return false
    nextType = typeOf(lookBackwards ? actions[actions.length - 1] : actions[0])
  }
  nextType = typeOf(nextType)
  return typeof nextType === 'string' && nextType.includes('addRowFromMap')
}
